import { acceptableMultiLineText, WHATSAPP_TEMPLATE_BODY_MAX, type LeadCardResponse, type LeadWhatsAppSendVia, type MyContext, type WhatsAppTemplateView } from "@gioia365/contracts";
import {
  dailyRecipientCap,
  renderTemplate,
  shouldWarnDailyCap,
  templatePurpose,
  waMeLink,
  whatsappRoute,
  withStopLine,
  type TemplateVariable,
  type WhatsAppRoute,
} from "@gioia365/domain/whatsapp";
import { createTranslator, isLocale, type Locale } from "@gioia365/i18n";
import { formatNumericDate, formatNumericTime } from "../i18n/format";
import { holderNames } from "../leads/card/card-fields";

// Il messaggio del WhatsApp a mano come lo compone la finestra (piano M6, R1, Task 4a; D4, D5, D25; ruling B2 ed E7): i cellulari
// a cui si scrive, le variabili del Lead con le regole del server (`apps/server/src/whatsapp/messages.ts`), il testo con la riga
// «scrivi STOP» dell'azienda in fondo e il link `wa.me`, tutto nel browser. Il testo che va nel link è quello che la preparazione
// registra (`editedText`): il server lo riempie di nuovo (non resta niente da riempire) e non rimette la riga, che c'è già. Dal
// piano M6, R2 (Task 9b), anche la scelta fra «Invia il WhatsApp» e il modo a mano e l'avviso del tetto, con le regole del dominio.

/** Un cellulare che riceve WhatsApp («A chi scrivi»), nell'ordine di `whatsapp.phoneIds` della scheda. */
export type Recipient = {
  phoneId: string;
  e164: string;
  holder: "primary" | "second";
  /** Nome e cognome del titolare di quel cellulare, se ci sono. */
  name: string | null;
  /** Il nome del titolare: il valore di `{nome}`. */
  firstName: string | null;
};

export function recipientsOf(card: LeadCardResponse): Recipient[] {
  return card.whatsapp.phoneIds.flatMap((phoneId) => {
    const phone = card.lead.phones.find((candidate) => candidate.id === phoneId);
    return phone ? [recipientOf(card, phone)] : [];
  });
}

/** Il telefono del Lead come destinatario, con il nome del suo titolare (anche un telefono da cui il Lead ha scritto, piano M6 R3). */
export function recipientOf(card: LeadCardResponse, phone: LeadCardResponse["lead"]["phones"][number]): Recipient {
  const { lead } = card;
  const names = holderNames(lead);
  const second = phone.holder === "second";
  const firstName = (second ? lead.secondHolder?.firstName : lead.firstName)?.trim() || null;
  return { phoneId: phone.id, e164: phone.e164, holder: phone.holder, name: second ? names.second : names.first, firstName };
}

const weekdayFormatters = new Map<string, Intl.DateTimeFormat>();

/** `{giorno e ora}` come lo scrive il server: il giorno della settimana nella lingua dell'azienda, la data e l'ora nel suo fuso. */
export function templateDayAndTime(at: string, settings: MyContext["settings"]): string {
  const locale: Locale = isLocale(settings.defaultLocale) ? settings.defaultLocale : "it";
  const { timeZone } = settings;
  const key = `${locale}|${timeZone}`;
  let weekday = weekdayFormatters.get(key);
  if (!weekday) {
    weekday = new Intl.DateTimeFormat(locale, { timeZone, weekday: "long" });
    weekdayFormatters.set(key, weekday);
  }
  return createTranslator(locale)("whatsapp.dayAndTime", {
    weekday: weekday.format(new Date(at)),
    date: formatNumericDate(timeZone, at),
    time: formatNumericTime(timeZone, at),
  });
}

/**
 * Le variabili per questo cellulare, con le fonti del server (ruling B2): `{nome}` il nome del titolare del cellulare,
 * `{AziendaTarget}` l'azienda del Lead, `{commerciale}` il nome di chi scrive (o la prima parola del suo nome intero),
 * `{NostraAzienda}` il nome dell'azienda, `{giorno e ora}` il richiamo aperto.
 * Una variabile senza valore resta fuori: il testo la tiene fra graffe e la finestra chiede di completarla (D5).
 */
export function templateValuesOf(card: LeadCardResponse, recipient: Recipient | null, context: MyContext): Partial<Record<TemplateVariable, string>> {
  const values: Partial<Record<TemplateVariable, string>> = { NostraAzienda: context.organization.name };
  if (recipient?.firstName) values.nome = recipient.firstName;
  if (card.lead.salonName) values.AziendaTarget = card.lead.salonName;
  const writer = context.firstName?.trim() || context.displayName?.trim().split(/\s+/)[0];
  if (writer) values.commerciale = writer;
  const callback = card.openTask?.status === "open" ? card.openTask : null;
  if (callback) values["giorno e ora"] = templateDayAndTime(callback.dueAt, context.settings);
  return values;
}

export type ComposedMessage = {
  /** Il testo che il Lead riceve, con la riga «scrivi STOP» in fondo (vuoto se il messaggio è vuoto). */
  text: string;
  /** Le variabili rimaste fra graffe, nell'ordine della prima comparsa. */
  missing: TemplateVariable[];
  empty: boolean;
  tooLong: boolean;
};

/**
 * Il messaggio dal testo della finestra: le variabili riempite, i caratteri che la preparazione rifiuterebbe tolti, senza spazi ai
 * bordi (come lo salva il server), poi la riga dell'azienda, una volta sola.
 */
export function composeMessage(draft: string, values: Partial<Record<TemplateVariable, string>>, stopLine: string): ComposedMessage {
  const rendered = renderTemplate(draft, values);
  const body = acceptableMultiLineText(rendered.text).trim();
  if (body === "") return { text: "", missing: rendered.missing, empty: true, tooLong: false };
  const text = stopLine.trim() === "" ? body : withStopLine(body, stopLine);
  return { text, missing: rendered.missing, empty: false, tooLong: text.length > WHATSAPP_TEMPLATE_BODY_MAX };
}

/**
 * Come parte il messaggio di un modello (piano M6, R2, Task 9b; ruling A4c, A4e): `whatsappRoute` del dominio con gli stessi fatti
 * del server (`chooseWhatsAppRoute` di `apps/server/src/whatsapp/route-choice.ts`), così il pulsante giusto è già lì al clic e il
 * server ricontrolla: il WhatsApp di chi scrive (`whatsapp.sendVia` della scheda, `null` per chi scrive a mano), se Meta ha approvato
 * il testo di adesso del modello (`approved`, oppure in revisione) e se ogni sua variabile ha un valore per questo cellulare anche
 * dopo la pulizia dei parametri (`complete`, da `templateParametersOf`). Come sul server, in R2 niente finestra di 24 ore né testo
 * libero (ruling B1) e la regola del Lead resta fuori: la dice la scheda (`whatsapp.available`), prima della scelta.
 *
 * Con WhatsApp Agile (piano M6, R9, Task 27b) i controlli dell'invio che la finestra conosce prima del clic: il tetto dei 2 al giorno,
 * dal conteggio di oggi della scheda (`todayCount`; le conferme passano, WA-10); il canale salvato è già in `sendVia.ready` (un «giù» di
 * meno di 10 minuti lo tiene a mano, la regola del dominio che la scheda applica). I numeri ammessi, il ritmo del numero e il canale
 * chiesto al fornitore li sa soltanto il server all'invio: la sua risposta li dice (`fallbackReasonText`). Con Agile il testo si ritocca
 * e un campo che resta vuoto lo dice la finestra (il server risponde `422`): `complete` lo passa chi chiama.
 */
export function whatsAppRouteOf(
  sendVia: LeadWhatsAppSendVia | null,
  template: Pick<WhatsAppTemplateView, "approved" | "metaStatus" | "category">,
  complete: boolean,
  todayCount = 0,
): WhatsAppRoute {
  const agile = sendVia?.channel === "agile" ? { recipientAllowed: true, capReached: !dailyRecipientCap(todayCount, templatePurpose(template.category)).allowed, pacedUntil: null, channel: "ok" as const } : undefined;
  return whatsappRoute({
    sender: sendVia,
    template: { approval: template.approved ? "approved" : template.metaStatus === "pending" ? "pending" : "none", complete },
    leadBlocked: null,
    withinServiceWindow: false,
    freeText: false,
    ...(agile === undefined ? {} : { agile }),
  });
}

/**
 * L'avviso del tetto prima del clic (questione 413), con la regola del dominio che usa anche il server: il Lead ha già ricevuto oggi
 * il massimo di WhatsApp (`whatsapp.todayCount` della scheda); una conferma (modello di categoria `utility`) conta ma non avvisa.
 */
export function dailyCapWarningOf(todayCount: number, template: Pick<WhatsAppTemplateView, "category">): boolean {
  return shouldWarnDailyCap(todayCount, templatePurpose(template.category));
}

/** Il link `wa.me` del cellulare; `null` per un numero che non è internazionale (un cellulare non valido, mai un errore). */
export function whatsAppLinkOf(e164: string, text: string): string | null {
  try {
    return waMeLink(e164, text);
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
}
